/**
 * ToolClusterGrid, themed clusters replacing the flat 9-tool grid.
 *
 * Groups the non-flagship tools into three semantic clusters so the
 * homepage no longer treats every tool as equal weight. Ask Wayly
 * is featured as the conversational entry at the bottom.
 *
 * Design decision: the two flagship tools (Statement Decoder + Invoice
 * Checker) live in the DualFlagshipHero above; this section covers the
 * remaining seven plus Ask Wayly's conversational surface.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle } from "lucide-react";
import { TOOLS_ORDERED } from "@/config/toolRegistry";

/** Cluster definitions, slug lists MUST match toolRegistry slugs. */
const CLUSTERS = [
    {
        id: "money-statements",
        title: "Money & Statements",
        blurb: "Anything to do with the dollars, from reading statements to checking rates and forecasting your budget.",
        tone: "sage",
        slugs: ["budget-calculator", "provider-price-checker", "contribution-estimator"],
    },
    {
        id: "care-coordination",
        title: "Care Coordination",
        blurb: "Aligning the paperwork with the people. Care plans, classifications, and the letters that get things moving.",
        tone: "clay",
        slugs: ["care-plan-reviewer", "classification-self-check", "letters-and-follow-ups"],
    },
];

const CLUSTER_TONE = {
    sage: {
        eyebrow: "text-primary-k",
        dot: "bg-sage",
        border: "border-sage/30",
        hover: "hover:border-primary-k hover:shadow-[0_18px_48px_rgba(15,86,72,0.18)]",
    },
    clay: {
        eyebrow: "text-clay-dark",
        dot: "bg-clay",
        border: "border-clay/30",
        hover: "hover:border-clay hover:shadow-[0_18px_48px_rgba(181,124,87,0.22)]",
    },
};

function ToolCard({ tool, tone }) {
    const Icon = tool.IconComponent;
    const panel = tone === "clay" ? "panel-clay" : tone === "teal" ? "panel-teal" : tone === "terracotta" ? "panel-terracotta" : "panel-sage";
    return (
        <Link
            to={tool.marketingRoute || tool.route}
            data-testid={`cluster-tool-${tool.slug}`}
            className={`group block rounded-xl ${panel} p-5 hover:brightness-[1.03] transition-colors`}
        >
            <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-white/60 inline-flex items-center justify-center shrink-0">
                    <Icon className="h-5 w-5 text-primary-k" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                    <h4 className="font-heading text-base text-primary-k">{tool.name}</h4>
                    <p className="mt-1.5 text-sm text-muted-k leading-relaxed line-clamp-3">{tool.short}</p>
                </div>
            </div>
            <span className="mt-4 inline-flex items-center gap-1 text-sm text-primary-k font-semibold group-hover:gap-2 transition-colors">
                Open Tool <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </span>
        </Link>
    );
}

export default function ToolClusterGrid() {
    const bySlug = Object.fromEntries(TOOLS_ORDERED.map((t) => [t.slug, t]));
    const askWayly = bySlug["family-coordinator"];

    return (
        <section className="mx-auto max-w-7xl px-6 py-10 sm:py-20 landing-marketing" data-testid="tool-cluster-grid">
            <span className="overline">Everything Else Wayly Does</span>
            <h2 className="font-heading text-3xl sm:text-5xl text-primary-k mt-3 tracking-tight lg:whitespace-nowrap">
                More Tools, <span style={{ color: "#A5512B" }}>Grouped the Way Families Think</span>.
            </h2>
            <p className="mt-3 text-base text-muted-k leading-relaxed">
                The Statement Decoder is your front door. Once you&apos;re inside, these are the tools waiting to help with the rest of your loved one&apos;s care.
            </p>

            <div className="mt-12 space-y-14" data-testid="clusters">
                {CLUSTERS.map((cluster) => {
                    const tone = CLUSTER_TONE[cluster.tone] || CLUSTER_TONE.sage;
                    const tools = cluster.slugs.map((s) => bySlug[s]).filter(Boolean);
                    return (
                        <div key={cluster.id} data-testid={`cluster-${cluster.id}`}>
                            <div className="flex-1 min-w-0">
                                <h3 className="font-heading text-3xl font-bold tracking-tight" style={{ color: "#A5512B" }}>{cluster.title}</h3>
                                <p className="mt-2 text-base text-muted-k leading-relaxed">{cluster.blurb}</p>
                            </div>
                            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {tools.map((t, ti) => (
                                    <ToolCard key={t.slug} tool={t} tone={["teal", "clay", "sage", "terracotta"][ti % 4]} />
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}
